import { cn } from "@sagot/ui";
import { useEffect, useRef, type SyntheticEvent, type ReactNode, type RefObject } from "react";
import { PANE_FOCUS_DELAY_MS } from "./register-constants";
import { BackGlyph } from "./register-icons";

/**
 * A new pane takes focus after its slide-in (mockup `rGo`): the first text field, else the heading, so keyboard and screen-reader
 * users start at the top of the new step. A focus already inside the new pane is left alone (the opening button is gone, so
 * focus sits on the page body or, in WebKit, on `main`): a visitor, a password manager or a script that has already put focus on
 * a control must not be pulled back to the first field half way through typing (the late timer used to send the second field's
 * text into the first one).
 */
export function usePaneFocus(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const root = ref.current;
      if (!root) return;
      if (root.contains(document.activeElement)) return;
      const target = root.querySelector<HTMLElement>("input:not([type=checkbox]):not(:disabled)") ?? root.querySelector<HTMLElement>("h1");
      target?.focus({ preventScroll: true });
    }, PANE_FOCUS_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [ref]);
}

interface RegisterPaneProps {
  /** The visitor came back to this pane: it slides in from the left. */
  back?: boolean;
  onSubmit: (event: SyntheticEvent) => void;
  children: ReactNode;
}

/** One step of the wizard (mockup `.r-pane`): a form that slides in; remounted per step by the page. */
export function RegisterPane({ back = false, onSubmit, children }: RegisterPaneProps) {
  const ref = useRef<HTMLFormElement>(null);
  usePaneFocus(ref);
  return (
    <form ref={ref} noValidate className={cn("r-pane", back && "back")} onSubmit={onSubmit}>
      {children}
    </form>
  );
}

/** The pane heading. Always the page's `h1` (one per page; the mockup uses `h2` for later panes), focusable for the step change. */
export function PaneTitle({ children }: { children: ReactNode }) {
  return (
    <h1 className="a-title" id="r-title" tabIndex={-1}>
      {children}
    </h1>
  );
}

interface RegisterNavProps {
  backLabel: string;
  onBack: () => void;
  /** The step's submit button. */
  children: ReactNode;
}

/** Back button on the left, the step's action on the right (mockup `.r-nav`). */
export function RegisterNav({ backLabel, onBack, children }: RegisterNavProps) {
  return (
    <div className="r-nav">
      <button type="button" className="r-back" onClick={onBack}>
        <BackGlyph />
        {backLabel}
      </button>
      {children}
    </div>
  );
}
